import type { SupabaseClient } from '@supabase/supabase-js';
import { InfrastructureError } from '@dma/shared';
import { permissionsForRole, type AuthorizedUser, type Role } from '@dma/auth';
import type { AuthRepository } from '../domain/repositories/AuthRepository.interface';
import type { AdminSession } from '../domain/entities/AdminSession.entity';
import { AuthenticationFailedError } from '../domain/errors/AuthenticationFailedError';

const PROFILES_TABLE = 'profiles';

interface ProfileRow {
  id: string;
  email: string;
  role: Role;
}

/**
 * Infrastructure implementation of AuthRepository, backed by Supabase Auth
 * (email/password) for identity and the `profiles` table (see
 * infra/supabase/migrations/002_create_profiles.sql) for role assignment.
 * Per docs/architecture/05-error-handling-strategy.md: raw Supabase errors
 * are caught here and never leak past this layer as-is — sign-in failures
 * become the domain-level AuthenticationFailedError, everything else
 * (network, missing profile row, unexpected DB error) becomes InfrastructureError.
 */
export class SupabaseAuthRepository implements AuthRepository {
  constructor(private readonly client: SupabaseClient) {}

  public async signIn(email: string, password: string): Promise<AdminSession> {
    const { data, error } = await this.client.auth.signInWithPassword({ email, password });
    if (error || !data.user) {
      throw new AuthenticationFailedError({ cause: error });
    }
    return this.loadSession(data.user.id, data.user.email ?? email);
  }

  public async signOut(): Promise<void> {
    const { error } = await this.client.auth.signOut();
    if (error) {
      throw new InfrastructureError('AUTH_SIGN_OUT_FAILED', 'خروج از حساب با مشکل مواجه شد.', {
        cause: error
      });
    }
  }

  public async getCurrentSession(): Promise<AdminSession | null> {
    const { data, error } = await this.client.auth.getSession();
    if (error) {
      throw new InfrastructureError('AUTH_SESSION_FETCH_FAILED', 'بررسی وضعیت ورود با مشکل مواجه شد.', {
        cause: error
      });
    }

    const user = data.session?.user;
    if (!user) {
      return null;
    }
    return this.loadSession(user.id, user.email ?? '');
  }

  private async loadSession(userId: string, fallbackEmail: string): Promise<AdminSession> {
    const { data, error } = await this.client
      .from(PROFILES_TABLE)
      .select('id, email, role')
      .eq('id', userId)
      .maybeSingle<ProfileRow>();

    if (error) {
      throw new InfrastructureError('AUTH_PROFILE_FETCH_FAILED', 'دریافت اطلاعات کاربر با مشکل مواجه شد.', {
        cause: error
      });
    }

    // Defensive default: a signed-in user with no profile row yet (e.g. the
    // `handle_new_user` trigger racing with the first sign-in) still gets a
    // session, at the least-privileged role, rather than being locked out
    // with an unexplained error.
    const role: Role = data?.role ?? 'viewer';
    const authorizedUser: AuthorizedUser = {
      id: userId,
      role,
      permissions: permissionsForRole(role)
    };

    return { user: authorizedUser, email: data?.email ?? fallbackEmail };
  }
}
